import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Camera, ExternalLink, AlertCircle, Upload, X,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';

/*
 * SitePhotos — what this place looks like.
 *
 * Two sources on one strip, and the difference between them is legal rather
 * than visual, which is why the strip labels them.
 *
 *   GOOGLE'S are borrowed. Places' policy exempts the place ID from its
 *   caching ban and nothing else, so each one is fetched fresh against the
 *   place ID we hold and stored by nobody — not in a column, not in a bucket,
 *   not in the browser cache. Google also requires that a displayed photo
 *   carry its author and a way to open the original, so the attribution line
 *   under each is not optional furniture.
 *
 *   OURS are kept. A picture somebody took standing in the car park is better
 *   evidence of what a rep walks into than a storefront shot, and it is the
 *   only kind that can show a loading dock or a comms room. They live in a
 *   private bucket and are served from behind the same gate as the site.
 *
 * A site with no place ID and no uploads shows the upload control and says
 * why there is nothing to see — an empty strip with no explanation reads as
 * broken.
 */

/*
 * An <img src> carries no Authorization header, and every photo route sits
 * behind `verifyAuth` — so a plain src would 401 on all of them, for both
 * sources. The alternatives are a signed URL (more machinery, and a token in
 * a query string that lands in logs and screenshots) or fetching the bytes
 * ourselves. We fetch.
 *
 * It also happens to be the only way to honour the terms on Google's half: a
 * blob URL is revoked when this unmounts, so the bytes are gone when the
 * reader closes the drawer. A cached <img> would be a store.
 */
function AuthImage({ src, alt }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoked = false;
    let objectUrl = null;
    setUrl(null);
    setFailed(false);

    (async () => {
      try {
        const token = await apiClient.getToken();
        const res = await fetch(`${apiClient.baseUrl}${src}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        if (revoked) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        if (!revoked) setFailed(true);
      }
    })();

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (failed) {
    return <span className="sites-photo-missing sites-muted" title="That image would not load">—</span>;
  }
  if (!url) return <span className="sites-photo-loading" aria-hidden />;
  return <img src={url} alt={alt} />;
}

function Attribution({ photo }) {
  if (photo.source !== 'google') {
    return photo.caption ? <figcaption className="sites-photo-cap">{photo.caption}</figcaption> : null;
  }
  const a = photo.attributions?.[0];
  return (
    <figcaption className="sites-photo-cap sites-muted">
      {a?.uri
        ? <a href={a.uri} target="_blank" rel="noreferrer">{a.name}</a>
        : (a?.name || 'Google')}
      {' · Google'}
    </figcaption>
  );
}

export default function SitePhotos({ site }) {
  const queryClient = useQueryClient();
  const fileInput = useRef(null);
  const [err, setErr] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['site-photos', site.siteId],
    queryFn: () => apiClient.get(`/api/sites/${site.siteId}/photos`),
    // Google's half is re-fetched by design — see the header — so there is no
    // point holding it long. Ours change only when somebody uploads one.
    staleTime: 60 * 1000,
  });

  const upload = useMutation({
    mutationFn: (file) => {
      const body = new FormData();
      body.append('photo', file);
      // Phones put the capture time in EXIF, which we do not parse; the file's
      // own mtime is the best honest answer and a human can correct it later.
      if (file.lastModified) body.append('takenAt', new Date(file.lastModified).toISOString());
      return apiClient.post(`/api/sites/${site.siteId}/photos`, body);
    },
    onSuccess: () => {
      setErr(null);
      queryClient.invalidateQueries({ queryKey: ['site-photos', site.siteId] });
    },
    onError: (e) => setErr(e?.data?.error || e.message || 'That upload did not go through'),
  });

  const remove = useMutation({
    mutationFn: (photoId) => apiClient.post(`/api/sites/${site.siteId}/photos/${photoId}/remove`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['site-photos', site.siteId] }),
    onError: (e) => setErr(e?.data?.error || e.message || 'Could not take that photo down'),
  });

  const photos = data?.photos || [];
  const hasGoogle = photos.some((p) => p.source === 'google');

  return (
    <section className="sites-photos">
      <div className="sites-photos-head">
        <h5><Camera size={14} aria-hidden /> Photos</h5>
        <button
          type="button"
          className="sites-btn-quiet"
          disabled={upload.isPending}
          onClick={() => fileInput.current?.click()}
        >
          <Upload size={13} aria-hidden /> {upload.isPending ? 'Uploading…' : 'Add one'}
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/avif"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            // Reset first: picking the same file twice in a row fires no
            // change event otherwise, and the second attempt looks ignored.
            e.target.value = '';
            if (file) upload.mutate(file);
          }}
        />
      </div>

      {isLoading && <p className="accounts-panel-note">Loading photos…</p>}

      {!isLoading && photos.length === 0 && (
        <p className="sites-muted">
          {site.googlePlaceId
            ? 'Google has no photos of this place. Add one you took yourself.'
            : 'No photos yet. This site has no Google listing pinned to it, so there is nothing to borrow — add one you took yourself, or correct the address to have it matched.'}
        </p>
      )}

      {photos.length > 0 && (
        <ul className="sites-photo-strip">
          {photos.map((p) => (
            <li key={p.photoId || p.ref}>
              <figure>
                <AuthImage src={p.url} alt={p.caption || site.siteName} />
                <Attribution photo={p} />
                {p.source === 'own' && (
                  <button
                    type="button"
                    className="sites-photo-remove"
                    title="Take this photo down. It is kept on record, not deleted."
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(p.photoId)}
                  >
                    <X size={12} aria-hidden />
                  </button>
                )}
              </figure>
            </li>
          ))}
        </ul>
      )}

      {hasGoogle && (
        <p className="sites-muted sites-photo-note">
          Google&apos;s photos are fetched each time you open this and are never stored —
          their terms let us keep the place ID and nothing else.
          {data?.googleMapsUri && (
            <>
              {' '}
              <a href={data.googleMapsUri} target="_blank" rel="noreferrer">
                See this place on Google Maps <ExternalLink size={11} aria-hidden />
              </a>
            </>
          )}
        </p>
      )}

      {data?.googleError && (
        <p className="sites-muted sites-photo-note">
          Google would not answer for photos this time: {data.googleError}
        </p>
      )}
      {err && <div className="accounts-error"><AlertCircle size={13} aria-hidden /> {err}</div>}
    </section>
  );
}
